import React, { createContext, useContext, useState } from 'react';
import { PhoneNumberDto } from '@/types/PhoneNumberDto.ts';

export interface UserDetails {
  name: string;
  surname: string;
  phoneNumber: PhoneNumberDto;
}

export interface CompanyData {
  companyName: string;
}

export interface CompleteProfileData {
  userDetails: UserDetails;
  companyData: CompanyData;
}

interface CompleteProfileContextType {
  profileData: CompleteProfileData;
  updateUserDetails: (data: UserDetails) => void;
  updateCompanyData: (data: CompanyData) => void;
}

const defaultProfileData: CompleteProfileData = {
  userDetails: {
    name: '',
    surname: '',
    phoneNumber: { dialCode: '+48', number: '' },
  },
  companyData: {
    companyName: '',
  },
};

const CompleteProfileContext = createContext<
  CompleteProfileContextType | undefined
>(undefined);

export const CompleteProfileProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [profileData, setProfileData] =
    useState<CompleteProfileData>(defaultProfileData);

  const updateUserDetails = (data: UserDetails) => {
    setProfileData((prev) => ({ ...prev, userDetails: data }));
  };

  const updateCompanyData = (data: CompanyData) => {
    setProfileData((prev) => ({ ...prev, companyData: data }));
  };

  return (
    <CompleteProfileContext.Provider
      value={{
        profileData,
        updateUserDetails,
        updateCompanyData,
      }}
    >
      {children}
    </CompleteProfileContext.Provider>
  );
};

export const useCompleteProfile = () => {
  const context = useContext(CompleteProfileContext);
  if (context === undefined) {
    throw new Error(
      'useCompleteProfile must be used within a CompleteProfileProvider',
    );
  }
  return context;
};
